// Every consequential change is confirmed first, naming what goes and what
// stays. Cancel takes focus so Enter never does the irreversible thing
// (DESIGN.web.md 7.5). The dialog stays open and busy until the call
// returns, its bar along its top edge and its buttons standing down: it closes on success, and on failure it goes back to idle so the
// toast and the question are both still there.

import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import type { ReactNode } from 'react';

import { TopLoadBar } from './top-load-bar';

export interface ConfirmOptions {
  title: ReactNode;
  body: ReactNode;
  /** The confirm button's words: the action, never "OK". */
  label: string;
  /** Vermillion on the confirm button, for what cannot be taken back lightly. */
  danger?: boolean;
  /** The change itself; the dialog is busy until it settles. */
  onConfirm: () => Promise<unknown>;
}

export function confirmFirst({ title, body, label, danger = false, onConfirm }: ConfirmOptions): void {
  const tone = danger ? { color: 'danger' } : {};
  const modalId = modals.openConfirmModal({
    title,
    children: <Text size="sm">{body}</Text>,
    labels: { confirm: label, cancel: 'Cancel' },
    closeOnConfirm: false,
    confirmProps: tone,
    cancelProps: { 'data-autofocus': true },
    // Mantine leaves md under the body; with sm above the hairline the body
    // sits xl over it, as a form's last field does. Buttons sm apart, as in
    // every other dialog.
    groupProps: { className: 'dialog-footer', mt: 'sm', gap: 'sm' },
    onConfirm: () => {
      // Busy along the dialog's top edge, both buttons standing down, never a spinner in the button.
      const busy = (on: boolean) => modals.updateModal({ modalId, children: <><TopLoadBar active={on} /><Text size="sm">{body}</Text></>, confirmProps: { ...tone, disabled: on }, cancelProps: { 'data-autofocus': true, disabled: on } });
      busy(true);
      onConfirm().then(
        () => modals.close(modalId),
        () => busy(false),
      );
    },
  });
}
